import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const RECT_PACKING_PARAMS = [
  {
    kind: 'int',
    key: 'depth',
    label: 'Rekursionstiefe',
    min: 1,
    max: 12,
    step: 1,
    default: 8,
    bulkRange: [4, 10],
    group: 'Unterteilung',
  },
  {
    kind: 'float',
    key: 'splitChance',
    label: 'Unterteilungs-Wahrscheinlichkeit',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.86,
    group: 'Unterteilung',
  },
  {
    kind: 'float',
    key: 'aspectBias',
    label: 'Seitenverhältnis-Bias',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.75,
    group: 'Unterteilung',
    tooltip: 'Hoch schneidet bevorzugt die längere Seite, was quadratischere Kacheln ergibt.',
  },
  {
    kind: 'float',
    key: 'splitJitter',
    label: 'Schnittstreuung',
    min: 0,
    max: 0.45,
    step: 0.01,
    default: 0.22,
    group: 'Unterteilung',
    tooltip: 'Wie weit der Schnitt von der Mitte abweichen darf.',
  },
  {
    kind: 'float',
    key: 'minSize',
    label: 'Mindestgrösse',
    min: 2,
    max: 200,
    step: 1,
    scale: 'log',
    default: 12,
    unit: 'px',
    group: 'Unterteilung',
  },
  {
    kind: 'float',
    key: 'textRefinement',
    label: 'Verfeinerung am Text',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.75,
    group: 'Unterteilung',
    tooltip: 'Teilt Rechtecke nahe der Kontur weiter, auch wenn sie sonst stehen blieben.',
  },
  {
    kind: 'float',
    key: 'refinementReach',
    label: 'Reichweite',
    min: 5,
    max: 400,
    step: 1,
    default: 90,
    unit: 'px',
    group: 'Unterteilung',
    showIf: (v) => (v.textRefinement as number) > 0,
  },
  {
    kind: 'enum',
    key: 'region',
    label: 'Bereich',
    default: 'outside',
    segmented: true,
    group: 'Unterteilung',
    options: [
      { value: 'outside', label: 'Aussen' },
      { value: 'inside', label: 'Innen' },
      { value: 'all', label: 'Überall' },
    ],
  },

  {
    kind: 'float',
    key: 'gap',
    label: 'Lücke',
    min: 0,
    max: 30,
    step: 0.25,
    default: 3,
    unit: 'px',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'radius',
    label: 'Eckenrundung',
    min: 0,
    max: 40,
    step: 0.5,
    default: 0,
    unit: 'px',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'fillChance',
    label: 'Füllwahrscheinlichkeit',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.42,
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Strichstärke',
    min: 0.2,
    max: 10,
    step: 0.1,
    default: 1.2,
    unit: 'px',
    group: 'Darstellung',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.9,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'size',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'size', label: 'Nach Grösse' },
      { value: 'depth', label: 'Nach Tiefe' },
      { value: 'distance', label: 'Nach Textdistanz' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'bauhaus' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type RectPackingValues = SchemaValues<typeof RECT_PACKING_PARAMS>;
